import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, TrendingUp, TrendingDown, Minus, ChevronRight, Search, Medal, Target, Users, ArrowUpDown, ArrowDown, ArrowUp, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';

type SortOption = 'points' | 'wins' | 'gd';

interface PlayerRankingEntry {
  id: string;
  name: string;
  slug: string;
  avatar: string | null;
  points: number;
  wins: number;
  draws: number;
  losses: number;
  matches: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  position?: string | null;
  gameId?: string | null;
}

export function PlayerRanking() {
  const [players, setPlayers] = useState<PlayerRankingEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('points');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const { data: leaderboardData } = await supabase
          .from('leaderboard')
          .select(`
            player_id, points, wins, draws, losses, matches_played, goals_for, goals_against,
            player:players(id, name, slug, avatar, position, profile_data:profiles!players_profile_id_fkey(game_id))
          `)
          .eq('period_type', 'all_time')
          .order('points', { ascending: false })
          .order('wins', { ascending: false })
          .order('goals_for', { ascending: false })
          .order('goals_against', { ascending: true });

        if (leaderboardData) {
          const formattedPlayers = leaderboardData
            .filter(entry => entry.player)
            .map(entry => {
              const playerData = entry.player as any;
              const goalsFor = entry.goals_for || 0;
              const goalsAgainst = entry.goals_against || 0;
              
              return {
                id: entry.player_id,
                name: playerData?.name || 'Unknown',
                slug: playerData?.slug || '',
                avatar: playerData?.avatar || null,
                points: entry.points || 0,
                wins: entry.wins || 0,
                draws: entry.draws || 0,
                losses: entry.losses || 0,
                matches: entry.matches_played || 0,
                goalsFor,
                goalsAgainst,
                goalDifference: goalsFor - goalsAgainst,
                position: playerData?.position || null,
                gameId: playerData?.profile_data?.game_id || null,
              };
            });
          
          setPlayers(formattedPlayers);
        }
      } catch (error) {
        console.error('Error fetching player ranking:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  // Sort and filter players
  const sortedAndFilteredPlayers = useMemo(() => {
    let result = [...players];
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) ||
        (p.gameId && p.gameId.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'wins':
          if (b.wins !== a.wins) return b.wins - a.wins;
          if (b.points !== a.points) return b.points - a.points;
          return b.goalDifference - a.goalDifference;
        case 'gd':
          if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
          if (b.points !== a.points) return b.points - a.points;
          return b.wins - a.wins;
        case 'points':
        default:
          if (b.points !== a.points) return b.points - a.points;
          if (b.wins !== a.wins) return b.wins - a.wins;
          return b.goalDifference - a.goalDifference;
      }
    });
    
    return result;
  }, [players, searchQuery, sortBy]);

  const displayedPlayers = showAll ? sortedAndFilteredPlayers : sortedAndFilteredPlayers.slice(0, 10);

  const getRankBadge = (rank: number) => {
    if (rank === 1) return <span className="text-xl">🥇</span>;
    if (rank === 2) return <span className="text-xl">🥈</span>;
    if (rank === 3) return <span className="text-xl">🥉</span>;
    return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  const getGDColor = (gd: number) => {
    if (gd > 0) return 'text-success';
    if (gd < 0) return 'text-loss';
    return 'text-muted-foreground';
  };

  // Loading state - just show button
  if (isLoading) {
    return (
      <section className="py-4">
        <div className="container">
          <Button
            variant="outline"
            size="lg"
            disabled
            className="w-full py-6 bg-muted/20 border-border/30"
          >
            <BarChart3 className="h-5 w-5 mr-2 animate-pulse" />
            Loading Player Ranking...
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4">
      <div className="container">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          {/* Toggle Button */}
          <CollapsibleTrigger asChild>
            <Button
              variant="outline"
              size="lg"
              className="w-full py-6 bg-gradient-to-r from-primary/5 via-gold/5 to-secondary/5 border-border/30 hover:border-primary/50 hover:bg-primary/10 transition-all group"
            >
              <div className="flex items-center justify-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <span className="font-semibold text-foreground">
                  Player Ranking
                </span>
                <Badge variant="secondary" className="ml-1">
                  {players.length} Players
                </Badge>
                {isOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground ml-2" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground ml-2" />
                )}
              </div>
            </Button>
          </CollapsibleTrigger>

          {/* Collapsible Content */}
          <CollapsibleContent className="mt-4">
            <Card variant="glass" className="overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-primary/10">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    Full Ranking
                  </CardTitle>
                  
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Sort Options */}
                    <ToggleGroup 
                      type="single" 
                      value={sortBy} 
                      onValueChange={(value) => value && setSortBy(value as SortOption)}
                      className="bg-muted/30 rounded-lg p-1"
                    >
                      <ToggleGroupItem 
                        value="points" 
                        size="sm"
                        className="data-[state=on]:bg-gold/20 data-[state=on]:text-gold px-3"
                      >
                        <Trophy className="h-3.5 w-3.5 mr-1.5" />
                        Points
                      </ToggleGroupItem>
                      <ToggleGroupItem 
                        value="wins" 
                        size="sm"
                        className="data-[state=on]:bg-success/20 data-[state=on]:text-success px-3"
                      >
                        <Medal className="h-3.5 w-3.5 mr-1.5" />
                        Wins
                      </ToggleGroupItem>
                      <ToggleGroupItem 
                        value="gd" 
                        size="sm"
                        className="data-[state=on]:bg-primary/20 data-[state=on]:text-primary px-3"
                      >
                        <Target className="h-3.5 w-3.5 mr-1.5" />
                        GD
                      </ToggleGroupItem>
                    </ToggleGroup>

                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search player..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-full md:w-48 bg-muted/20 border-border/30"
                      />
                    </div>
                    
                    <Button asChild variant="ghost" size="sm" className="hover:bg-primary/10 hover:text-primary hidden md:flex">
                      <Link to="/leaderboard">
                        Full Leaderboard
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                {/* Header Row - Desktop */}
                <div className="hidden md:grid grid-cols-12 gap-2 px-6 py-3 bg-muted/30 border-y border-border/20 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <div className="col-span-1 text-center">#</div>
                  <div className="col-span-4">Player</div>
                  <div className="col-span-1 text-center">M</div>
                  <div className="col-span-1 text-center">W</div>
                  <div className="col-span-1 text-center">D</div>
                  <div className="col-span-1 text-center">L</div>
                  <div className="col-span-1 text-center">GF</div>
                  <div className="col-span-1 text-center">GD</div>
                  <div className="col-span-1 text-center">PTS</div>
                </div>

                {/* Player Rows */}
                <div className="divide-y divide-border/20 max-h-[500px] overflow-y-auto">
                  {displayedPlayers.length > 0 ? (
                    displayedPlayers.map((player, index) => {
                      const actualRank = players.findIndex(p => p.id === player.id) + 1;
                      
                      return (
                        <Link
                          key={player.id}
                          to={`/players/${player.slug}`}
                          className="block hover:bg-muted/30 transition-colors"
                        >
                          {/* Desktop View */}
                          <div className="hidden md:grid grid-cols-12 gap-2 px-6 py-4 items-center">
                            <div className="col-span-1 flex justify-center">
                              {getRankBadge(actualRank)}
                            </div>
                            <div className="col-span-4 flex items-center gap-3">
                              <Avatar className="h-10 w-10 ring-2 ring-border/30">
                                <AvatarImage src={player.avatar || undefined} />
                                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                  {player.name.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-semibold text-foreground">{player.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {player.gameId || player.position || 'Player'}
                                </div>
                              </div>
                            </div>
                            <div className="col-span-1 text-center font-mono text-sm">{player.matches}</div>
                            <div className="col-span-1 text-center font-mono text-sm text-success">{player.wins}</div>
                            <div className="col-span-1 text-center font-mono text-sm text-warning">{player.draws}</div>
                            <div className="col-span-1 text-center font-mono text-sm text-loss">{player.losses}</div>
                            <div className="col-span-1 text-center font-mono text-sm">{player.goalsFor}</div>
                            <div className={`col-span-1 text-center font-mono text-sm font-semibold ${getGDColor(player.goalDifference)}`}>
                              {player.goalDifference > 0 ? '+' : ''}{player.goalDifference}
                            </div>
                            <div className="col-span-1 text-center">
                              <span className="font-mono text-lg font-bold text-gold">{player.points}</span>
                            </div>
                          </div>

                          {/* Mobile View */}
                          <div className="md:hidden p-4">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-8 text-center">
                                {getRankBadge(actualRank)}
                              </div>
                              <Avatar className="h-12 w-12 ring-2 ring-border/30">
                                <AvatarImage src={player.avatar || undefined} />
                                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                  {player.name.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-foreground truncate">{player.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {player.gameId || player.position || 'Player'}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-mono text-xl font-bold text-gold">{player.points}</div>
                                <div className="text-xs text-muted-foreground">pts</div>
                              </div>
                            </div>
                            
                            {/* Mobile Stats Row */}
                            <div className="grid grid-cols-6 gap-2 mt-3 pt-3 border-t border-border/20 text-center">
                              <div>
                                <div className="text-xs text-muted-foreground">M</div>
                                <div className="font-mono text-sm">{player.matches}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">W</div>
                                <div className="font-mono text-sm text-success">{player.wins}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">D</div>
                                <div className="font-mono text-sm text-warning">{player.draws}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">L</div>
                                <div className="font-mono text-sm text-loss">{player.losses}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">GF</div>
                                <div className="font-mono text-sm">{player.goalsFor}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground">GD</div>
                                <div className={`font-mono text-sm font-semibold ${getGDColor(player.goalDifference)}`}>
                                  {player.goalDifference > 0 ? '+' : ''}{player.goalDifference}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-muted/30 mb-3">
                        <Users className="h-7 w-7 opacity-40" />
                      </div>
                      <p className="text-sm font-medium">
                        {searchQuery ? 'No players found' : 'No player data available'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Show More/Less Button */}
                {sortedAndFilteredPlayers.length > 10 && (
                  <div className="p-4 border-t border-border/20 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAll(!showAll)}
                      className="min-w-32"
                    >
                      {showAll ? (
                        <>Show Less</>
                      ) : (
                        <>Show All ({sortedAndFilteredPlayers.length} Players)</>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </section>
  );
}
